import React, { useCallback, useRef, useEffect, useState } from 'react';
import { Formik, useFormikContext } from 'formik';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { rejectionTypeToPolicyTypeMap, errorClassificationLookup } from '@ourbranch/lookups';
import { policyTypes } from '@ourbranch/policy-types';
import * as Sentry from '@sentry/react';

import { track } from 'core/helpers/analytics';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { Loading } from 'core/components/loading';
import { useToast } from 'core/components/toast';
import { useQueryParams } from 'core/hooks/useQueryParams';
import { phoneNumberFormatter } from 'core/helpers/formatters';
import { getNestedErrors } from 'quote/helpers/get-nested-errors';
import { updateUserInputWithConnectedHomeValues } from 'quote/helpers/get-connected-home-new-sign-up-values';
import { AddCar } from '../add-car';
import { ApplicantDetails } from '../applicant-details';
import { PersonalDetails } from '../personal-details';
import { AutoDetails } from '../auto-details';
import { Footer } from '../footer';
import { PriorAddress } from '../prior-address';
import { PropertyDetail } from '../property-detail';
import { PrimaryLocationDetails } from '../primary-location-details';
import { OfferDetails } from '../offer-details';
import { Disclaimer } from 'common/components/disclaimer';
import { getInitialValues } from './initial-values';
import { updateFormFields } from './update-form-fields';
import { handleCleanUp, codeSectionMatch } from './quote-forms.clean';
import { validateQuoteForm } from './quote-forms.validations';
import { OfferBranchPlusModal } from '../offer-branch-plus-modal';
import useStyles from './quote-forms.styles';

const DISMISSIBLE_REJECTION_CODES = [50055, 50096, 50097, 50098];

const ScrollToError = () => {
  const { errors, isSubmitting, setFieldTouched } = useFormikContext();

  // this useEffect is for when we click "resubmit offer with accurate details" on an offer that has alerts.
  // this will scroll to the already existing error alert, whereas the other hook in this file will scroll to alerts
  // that pop up after the form is submitted
  useEffect(() => {
    [
      'error-alert-applicant-details',
      'error-alert-offer-details',
      'error-alert-personal-details',
      'error-alert-prior-address',
      'error-alert-auto-details',
      'error-alert-add-cars',
      'error-alert-property-details'
    ].every((id) => {
      const element = document.getElementById(id);
      if (element) {
        // using set timeout because the first time this runs, the element isnt rendered yet so there is no
        // alert to scroll to, even though the element exists
        setTimeout(() => {
          element.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }, 100);
        return false;
      }
      return true;
    });
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length && isSubmitting) {
      const smoothScrollToElementById = (id) => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      };
      const errorPaths = getNestedErrors(errors);
      // touch all nested paths
      for (const path of errorPaths) {
        path.includes('.') && setFieldTouched(path);
      }

      // Function to map complex field paths to section error alert IDs
      const getErrorSectionId = (errorPath) => {
        // Map farm entity errors to their field IDs for direct field scrolling
        if (errorPath.startsWith('entity.')) {
          return errorPath;
        }
        // Map farm field errors to property details section
        if (errorPath.includes('farmLocations') || errorPath.includes('.risks[') || errorPath.includes('.building.')) {
          return 'error-alert-property-details';
        }
        // Add other field-to-section mappings as needed
        return errorPath; // Default: try original path as ID
      };

      try {
        if (errorPaths?.length > 1 && errorPaths[0] === 'fcraDisclaimer') {
          const sectionId = getErrorSectionId(errorPaths[1]);
          smoothScrollToElementById(sectionId);
        } else {
          const sectionId = getErrorSectionId(errorPaths[0]);
          smoothScrollToElementById(sectionId);
        }
      } catch {
        // something went wrong just scroll to top
        smoothScrollToElementById('firstName');
      }
    }
  }, [errors, isSubmitting, setFieldTouched]);
  return null;
};

const formatAdditionalPhoneNumbers = (additionalPhoneNumbers) => {
  return additionalPhoneNumbers
    .filter((phoneDetail) => phoneDetail.phoneNumber)
    .map((phoneDetail) => {
      return {
        phoneNumber: phoneNumberFormatter({ phoneNumber: phoneDetail.phoneNumber }),
        note: phoneDetail?.note ? phoneDetail.note.trim() : '',
        canText: phoneDetail.canText
      };
    });
};

const getOpenSections = (errors) => {
  return errors.reduce((openSections, error) => [...openSections, ...codeSectionMatch(Number(error.code))], []);
};

const QuoteForms = () => {
  const {
    quote: {
      requestQuoteAction,
      requestBranchPlusQuoteAction,
      getOfferDetails,
      errors,
      offer,
      loading,
      rejections,
      prefillData,
      fetchAffinityData,
      getBlocks
    },
    affinityLookups
  } = useStore();

  const blocks = getBlocks();
  const history = useHistory();
  const classes = useStyles({ rejections: rejections.length, blocks: blocks.length });
  const form = useRef(null);
  const params = useQueryParams();
  const toast = useToast();

  const { allStates, user, isAgency, canWriteMultipleAffinityCodes, unlicensedStates, allowedStates, isInternalSales } =
    useSession();

  // these states added to remotely collapse / expand two of the collapsible cards based on the
  // value of isNewConstruction or isNewPurchase( only uses Prior Address)
  const [showPriorAddressCallout, setShowPriorAddressCallout] = useState(false);
  const [showPropertyDetailsCallout, setShowPropertyDetailsCallout] = useState(false);
  const [sectionsToShow, setSectionsToShow] = useState(errors ? getOpenSections(errors) : []);
  const [showAllSectionsClicked, setShowAllSectionsClicked] = useState(false);
  const [hasDisabledAffinity, setHasDisabledAffinity] = useState(false);
  const [requestedPolicyType, setRequestedPolicyType] = useState(null);
  const [showOfferBranchPlusModal, setShowOfferBranchPlusModal] = useState(false);
  const [branchPlusOnlyMode, setBranchPlusOnlyMode] = useState(false);

  useEffect(() => {
    if (params.affinity && !affinityLookups.loading) {
      affinityLookups.getByAffinity(params.affinity);
    }
  }, [affinityLookups, params]);

  const handleSubmit = useCallback(
    (branchPlusOnlyMode) => async (values, formikBag) => {
      setRequestedPolicyType(values?.policyType);
      const affinityData = affinityLookups.data?.find(({ affinity }) => affinity === values.affinity);

      // track if unlicensed users are creating quotes
      if (unlicensedStates?.includes(values.state)) {
        track('Unlicensed user created quote', {
          agent: { ...user, unlicensedStates, allowedStates },
          quote: values
        });
      }
      if (values?.optIntoMarketingCampaigns) {
        track('Member Opted Into Marketing Campaigns', {
          email: values?.email,
          phone: values?.phone
        });
      }

      // send in values with connected home toggled on for affinity codes that are home security partners
      if (affinityData?.data.homeSecurity) {
        values = updateUserInputWithConnectedHomeValues(values, affinityData);
      }

      if (values.additionalPhoneNumbers?.length) {
        values.additionalPhoneNumbers = formatAdditionalPhoneNumbers(values.additionalPhoneNumbers);
      }

      if (values.phone?.length) {
        values.phone = phoneNumberFormatter({ phoneNumber: values.phone.toString() });
      }

      values.isApartment = Boolean(values.policyType?.includes('R'));

      const offerTakesLongNotificationTimeout = setTimeout(() => {
        toast.notify({
          type: 'success',
          message: "Please wait, we're still working on submitting your offer...",
          // GraphQL server timeout is 30s and we retry 3 times. The idea is that this toast should float
          // until we either get an offer or an error at which point we clear it with toast.clean() lower.
          durationInSeconds: 300
        });
      }, 20000);

      // Check for validation errors before submitting
      if (formikBag.errors && Object.keys(formikBag.errors).length > 0) {
        console.log('Form submission blocked due to validation errors:', formikBag.errors);
        toast.clean();
        clearTimeout(offerTakesLongNotificationTimeout);
        return; // Prevent submission when validation errors exist
      }

      if (branchPlusOnlyMode) {
        await requestBranchPlusQuoteAction(values, allStates);
      } else {
        await requestQuoteAction(values, allStates);
      }

      toast.clean();
      clearTimeout(offerTakesLongNotificationTimeout);

      handleCleanUp(
        errors,
        sectionsToShow,
        values.isNewConstruction,
        values.isNewPurchase,
        formikBag.setFieldValue,
        setSectionsToShow,
        showAllSectionsClicked
      );

      const { offer, alerts, blocks, rejections } = getOfferDetails();

      if (branchPlusOnlyMode && offer?.branchPlusOffers?.length > 0 && rejections.length === 0) {
        if (offer.branchPlusOffers.some((offer) => offer.status === 'success')) {
          history.push(`/offer/${offer.id}`);
        }
      }

      const hasNoValidPolicyTypes = values.policyType.split('').every((policyType) => {
        return rejections.some((rejection) => {
          const isRelevantPolicyType = rejectionTypeToPolicyTypeMap[rejection.type].includes(policyType);
          const isAlert = ['ALERT', 'DISMISSIBLE_REJECTION', 'BYPASS_ALERT'].includes(
            errorClassificationLookup[Number(rejection.code)]
          );
          return isRelevantPolicyType && rejection.level !== 'soft' && !isAlert;
        });
      });

      const branchPlusOnlyErrorsPresent = errors.some((error) => [5008, 5027].includes(Number(error.code)));
      if (
        (branchPlusOnlyErrorsPresent || hasNoValidPolicyTypes) &&
        !isAgency &&
        isInternalSales &&
        !branchPlusOnlyMode
      ) {
        setShowOfferBranchPlusModal(true);
      }

      // logic to fill out this form with data received back from rater
      if (offer?.quote) {
        updateFormFields(offer.quote, formikBag.setFieldValue, values);
      }

      // logic to redirect to modify offer page if successful offer received
      if (!offer?.options?.length > 0) {
        return;
      }
      if (alerts.length > 0) {
        // if any of the alerts are not bypass alerts or are for the requested policy type, then we should not push to offer
        const validAlerts = alerts.some((alert) => {
          const alertType = errorClassificationLookup[Number(alert.code)];
          if (alertType !== 'BYPASS_ALERT' || rejectionTypeToPolicyTypeMap[alert.type]?.includes(values.policyType)) {
            return true;
          }
          return false;
        });

        if (validAlerts) {
          return;
        }
      }
      if (blocks.length > 0) {
        return;
      }
      // if any of the rejections have a type that rejects our chosen policyType, then we should not push to offer
      const requestedPolicyTypeIsRejected = rejections?.some((rejection) => {
        if (rejection.level === 'soft' && !DISMISSIBLE_REJECTION_CODES.includes(rejection.type)) {
          return false;
        }
        return rejectionTypeToPolicyTypeMap[rejection.type]?.some((type) => type === values.policyType);
      });
      if (requestedPolicyTypeIsRejected) {
        return;
      }
      history.push(`/offer/${offer.id}`);
    },
    [
      requestQuoteAction,
      requestBranchPlusQuoteAction,
      allStates,
      affinityLookups,
      history,
      getOfferDetails,
      errors,
      sectionsToShow,
      showAllSectionsClicked,
      setSectionsToShow,
      toast,
      user,
      allowedStates,
      unlicensedStates,
      isAgency,
      isInternalSales
    ]
  );

  const validate = useCallback(
    (values) => {
      const { alerts } = getOfferDetails();

      return validateQuoteForm(
        errors.map(({ code }) => Number(code)),
        values,
        allStates,
        isAgency,
        alerts
      )
        .then((result) => {
          return result;
        })
        .catch((e) => {
          // log so we can quickly see validation issues in production
          if (e?.name?.includes('TypeError')) {
            Sentry.captureException(e, {
              extra: { formValues: values, inNewQuoteForm: true, validationFailedToRun: true }
            });
          } else {
            console.warn('[QuoteForms] Validation errors blocking submission:', JSON.stringify(e, null, 2));
            return e;
          }
        });
    },
    [errors, allStates, isAgency, getOfferDetails]
  );

  const prefill = { ...offer?.quote, ...prefillData, ...params, affinity: params.affinity?.replaceAll(' ', '') };
  const initialValues = getInitialValues(prefill, user, requestedPolicyType);

  useEffect(() => {
    const affinityData = affinityLookups.data?.find(({ affinity }) => affinity === user.affinityCode);

    if (affinityData?.disabled) {
      setHasDisabledAffinity(true);
    }
  }, [affinityLookups.data, user.affinityCode]);

  useEffect(() => {
    if (canWriteMultipleAffinityCodes) {
      affinityLookups.getByAffinityList(user.viewableAffinityCodes, 'ALL');
    } else {
      fetchAffinityData(user.affinityCode);
    }
  }, []);

  return (
    <div
      className={classNames(classes.container, {
        [classes.withRejections]: rejections.length > 0 || blocks.length > 0
      })}
    >
      <div className={classes.row}>
        <Label type="title" className={classes.title}>
          Create a New Offer
        </Label>
        {(!!offer || branchPlusOnlyMode) && (
          <Button
            className={classes.floatingButton}
            variant="contained"
            color="secondary"
            type="button"
            // this will force the page to reload, clearing the form AND the quote store
            onClick={() => history.go(0)}
          >
            Clear this offer
          </Button>
        )}
      </div>
      {affinityLookups.loading ? (
        <Loading noBackground />
      ) : (
        <Formik
          innerRef={form}
          initialValues={initialValues}
          onSubmit={handleSubmit(branchPlusOnlyMode)}
          validate={validate}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {({ values, setFieldValue }) => {
            return (
              <>
                {showOfferBranchPlusModal && (
                  <OfferBranchPlusModal
                    open={showOfferBranchPlusModal}
                    onClose={() => setShowOfferBranchPlusModal(false)}
                    submitBranchPlus={() => {
                      setShowOfferBranchPlusModal(false);
                      setBranchPlusOnlyMode(true);
                      handleSubmit(true)(values, { setFieldValue });
                    }}
                  />
                )}
                <ApplicantDetails
                  setShowPriorAddressCallout={setShowPriorAddressCallout}
                  setShowPropertyDetailsCallout={setShowPropertyDetailsCallout}
                  sectionsToShow={sectionsToShow}
                  setSectionsToShow={setSectionsToShow}
                  showAllSectionsClicked={showAllSectionsClicked}
                  setShowAllSectionsClicked={setShowAllSectionsClicked}
                />
                {/* Farm - Offer Details */}
                {values.policyType === policyTypes.Farm && <OfferDetails />}
                <PersonalDetails sectionsToShow={sectionsToShow} />
                <PriorAddress showPriorAddressCallout={showPriorAddressCallout} sectionsToShow={sectionsToShow} />
                <AutoDetails sectionsToShow={sectionsToShow} />
                <AddCar sectionsToShow={sectionsToShow} />

                {/* Farm */}
                <PrimaryLocationDetails sectionsToShow={sectionsToShow} />
                {/* End Farm */}

                {/* Home, Condo, Farm Property Details */}
                <PropertyDetail
                  showPropertyDetailsCallout={showPropertyDetailsCallout}
                  sectionsToShow={sectionsToShow}
                />

                <Disclaimer />
                <ScrollToError />
                <Footer
                  disableButton={Boolean(loading || affinityLookups.loading || hasDisabledAffinity)}
                  branchPlusOnlyMode={branchPlusOnlyMode}
                />
              </>
            );
          }}
        </Formik>
      )}
    </div>
  );
};

export default observer(QuoteForms);
